import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Truck, Plus, Loader2 } from "lucide-react";

const inputCls = "bg-black border-zinc-700 rounded-sm focus:border-[#FF5A00] focus:ring-0 text-sm";

export default function StandSupplierOrdersSection({ stand, onCreated }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/supplier-orders", { params: { stand_id: stand.id } });
      setOrders(data || []);
    } catch (e) { toast.error("No pude cargar los pedidos del stand"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stand.id]);

  return (
    <Card data-testid="stand-supplier-orders" className="bg-zinc-900 border-zinc-800 rounded-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF5A00]" /> Pedidos a proveedores generados</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="stand-po-create-btn" size="sm" className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                <Plus className="w-4 h-4 mr-1.5" /> Crear pedido a proveedor
              </Button>
            </DialogTrigger>
            <CreatePODialog stand={stand} onCreated={() => { setOpen(false); load(); onCreated && onCreated(); }} />
          </Dialog>
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</div>
        ) : orders.length === 0 ? (
          <div className="text-xs text-zinc-500">Todavía no generaste pedidos a proveedores para este stand.</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between border border-zinc-800 rounded-sm p-2 bg-zinc-950 text-xs">
                <div>
                  <div className="font-mono text-white">{o.po_number}</div>
                  <div className="text-zinc-500">{o.supplier_name || "Sin proveedor"} · {(o.items || []).length} ítems · {o.status}</div>
                </div>
                <div className="text-right font-mono text-zinc-300">{(o.totals?.total_final || 0).toLocaleString()} {o.moneda}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CreatePODialog({ stand, onCreated }) {
  const [scope, setScope] = useState("item");
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierWa, setSupplierWa] = useState("");
  const [moneda, setMoneda] = useState("ARS");
  const [anticipo, setAnticipo] = useState(0);
  const [fechaVenc, setFechaVenc] = useState("");
  const [condPago, setCondPago] = useState("");
  const [obsProv, setObsProv] = useState("");
  const [obsInt, setObsInt] = useState("");
  const [selectedIds, setSelectedIds] = useState({});
  const [itemsData, setItemsData] = useState({}); // { key: {cantidad, costo_unit, precio_venta} }
  const [manualLines, setManualLines] = useState([]); // [{id, name, unit, cantidad, costo_unit, precio_venta_cliente_unit}]
  const [saving, setSaving] = useState(false);

  const elementos = ((stand.configuration?.paquete?.elementos) || []).filter((e) => e.visible_to_client && Number(e.current_qty) > 0);
  const manuales = ((stand.configuration?.paquete?.items_manuales) || []);

  const options = useMemo(() => {
    const opts = [];
    for (const e of elementos) {
      opts.push({ key: `pe:${e.item_id}`, source: "package_element", ref: e.item_id, name: e.name, unit: e.unit, maxQty: e.current_qty });
    }
    for (const m of manuales) {
      opts.push({ key: `mi:${m.id}`, source: "manual_item", ref: m.id, name: m.name, unit: m.unit || "u", maxQty: m.cantidad, precio_hint: m.valor_unit_venta });
    }
    return opts;
  }, [elementos, manuales]);

  const toggle = (key) => setSelectedIds((s) => ({ ...s, [key]: !s[key] }));
  const updateItem = (key, field, val) => setItemsData((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: val } }));

  const addManualLine = () => setManualLines((ls) => [...ls, { id: `ml_${Date.now()}_${ls.length}`, name: "", unit: "u", cantidad: 1, costo_unit: 0, precio_venta_cliente_unit: "" }]);
  const updateManualLine = (id, field, val) => setManualLines((ls) => ls.map((l) => l.id === id ? { ...l, [field]: val } : l));
  const removeManualLine = (id) => setManualLines((ls) => ls.filter((l) => l.id !== id));

  const submit = async () => {
    if (!supplierName.trim()) return toast.error("Ingresá el nombre del proveedor");
    const selectedKeys = options.filter((o) => selectedIds[o.key]);
    const validManual = manualLines.filter((l) => l.name.trim() && Number(l.cantidad) > 0);
    if (selectedKeys.length === 0 && validManual.length === 0 && scope !== "proyecto_completo") {
      return toast.error("Seleccioná al menos un ítem o agregá una línea manual");
    }
    let items;
    if (scope === "proyecto_completo") {
      items = [{ source: "custom", name: `Proyecto completo · Stand ${stand.stand_number}`, cantidad: 1, unit: "u",
                 costo_unit: Number(itemsData.__full__?.costo_unit || 0) }];
    } else {
      const fromStand = selectedKeys.map((o) => ({
        source: o.source, source_ref_id: o.ref, name: o.name, unit: o.unit,
        cantidad: Number(itemsData[o.key]?.cantidad || o.maxQty),
        costo_unit: Number(itemsData[o.key]?.costo_unit || 0),
        precio_venta_cliente_unit: itemsData[o.key]?.precio_venta_cliente_unit ? Number(itemsData[o.key].precio_venta_cliente_unit) : (o.precio_hint ?? null),
        iva_rate: 21,
      }));
      const fromManual = validManual.map((l) => ({
        source: "custom", name: l.name.trim(), unit: l.unit || "u",
        cantidad: Number(l.cantidad || 0),
        costo_unit: Number(l.costo_unit || 0),
        precio_venta_cliente_unit: l.precio_venta_cliente_unit === "" ? null : Number(l.precio_venta_cliente_unit),
        iva_rate: 21,
      }));
      items = [...fromStand, ...fromManual];
    }
    setSaving(true);
    try {
      await api.post("/admin/supplier-orders", {
        stand_id: stand.id, supplier_name: supplierName, supplier_email: supplierEmail, supplier_whatsapp: supplierWa,
        scope, items, moneda, anticipo_pct: Number(anticipo || 0), fecha_vencimiento: fechaVenc,
        condiciones_pago: condPago, observaciones_para_proveedor: obsProv, observaciones_internas: obsInt,
      });
      toast.success("Pedido a proveedor creado");
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Crear pedido a proveedor · Stand {stand.stand_number}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Alcance</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="po-scope" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 rounded-sm">
                <SelectItem value="item">Ítem individual</SelectItem>
                <SelectItem value="grupo">Grupo de ítems</SelectItem>
                <SelectItem value="proyecto_completo">Proyecto completo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Moneda</Label>
            <Select value={moneda} onValueChange={setMoneda}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 rounded-sm">
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Anticipo %</Label>
            <Input type="number" className={inputCls} value={anticipo} onChange={(e) => setAnticipo(e.target.value)} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Proveedor</Label>
            <Input data-testid="po-supplier-name" className={inputCls} value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Razón social" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</Label>
            <Input className={inputCls} value={supplierEmail} onChange={(e) => setSupplierEmail(e.target.value)} placeholder="operaciones@proveedor.com" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">WhatsApp</Label>
            <Input className={inputCls} value={supplierWa} onChange={(e) => setSupplierWa(e.target.value)} placeholder="+54 9 …" />
          </div>
        </div>

        {scope !== "proyecto_completo" && (
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4">
              <div className="text-xs font-semibold mb-2">Seleccioná ítems del stand (opcional)</div>
              {options.length === 0 ? (
                <div className="text-xs text-zinc-500">Este stand no tiene ítems del paquete. Agregá líneas manuales debajo.</div>
              ) : (
                <div className="space-y-2">
                  {options.map((o) => (
                    <div key={o.key} className="border border-zinc-800 rounded-sm p-2 bg-zinc-950 text-xs">
                      <div className="flex items-center gap-2 mb-2">
                        <input type="checkbox" data-testid={`po-item-${o.key}`} checked={!!selectedIds[o.key]} onChange={() => toggle(o.key)} />
                        <div className="flex-1"><span className="text-white">{o.name}</span> <span className="text-zinc-500">· disponible {o.maxQty}{o.unit}</span></div>
                      </div>
                      {selectedIds[o.key] && (
                        <div className="grid md:grid-cols-3 gap-2 pl-6">
                          <div>
                            <Label className="text-[10px] text-zinc-500">Cantidad</Label>
                            <Input type="number" className={inputCls} value={itemsData[o.key]?.cantidad ?? o.maxQty} onChange={(e) => updateItem(o.key, "cantidad", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">Costo unit</Label>
                            <Input type="number" className={inputCls} value={itemsData[o.key]?.costo_unit || ""} onChange={(e) => updateItem(o.key, "costo_unit", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">Precio cliente (interno)</Label>
                            <Input type="number" className={inputCls} placeholder={o.precio_hint ? String(o.precio_hint) : ""} value={itemsData[o.key]?.precio_venta_cliente_unit || ""} onChange={(e) => updateItem(o.key, "precio_venta_cliente_unit", e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {scope !== "proyecto_completo" && (
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="po-manual-lines">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold">Líneas manuales / ad-hoc</div>
                <Button type="button" size="sm" data-testid="po-add-manual-line" onClick={addManualLine} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Agregar línea
                </Button>
              </div>
              {manualLines.length === 0 ? (
                <div className="text-xs text-zinc-500">Sumá cualquier ítem que no venga del paquete del stand: mobiliario, servicios técnicos, etc.</div>
              ) : (
                <div className="space-y-2">
                  {manualLines.map((l, idx) => (
                    <div key={l.id} className="border border-zinc-800 rounded-sm p-2 bg-zinc-950">
                      <div className="grid md:grid-cols-12 gap-2 items-end">
                        <div className="md:col-span-4">
                          <Label className="text-[10px] text-zinc-500">Nombre del ítem</Label>
                          <Input data-testid={`po-manual-name-${idx}`} className={inputCls} value={l.name} onChange={(e) => updateManualLine(l.id, "name", e.target.value)} placeholder="Ej: Sillas plegables" />
                        </div>
                        <div className="md:col-span-1">
                          <Label className="text-[10px] text-zinc-500">Unidad</Label>
                          <Input className={inputCls} value={l.unit} onChange={(e) => updateManualLine(l.id, "unit", e.target.value)} placeholder="u" />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[10px] text-zinc-500">Cantidad</Label>
                          <Input type="number" data-testid={`po-manual-qty-${idx}`} className={inputCls} value={l.cantidad} onChange={(e) => updateManualLine(l.id, "cantidad", e.target.value)} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[10px] text-zinc-500">Costo unit</Label>
                          <Input type="number" data-testid={`po-manual-cost-${idx}`} className={inputCls} value={l.costo_unit} onChange={(e) => updateManualLine(l.id, "costo_unit", e.target.value)} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[10px] text-zinc-500">Precio cliente</Label>
                          <Input type="number" className={inputCls} value={l.precio_venta_cliente_unit} onChange={(e) => updateManualLine(l.id, "precio_venta_cliente_unit", e.target.value)} placeholder="Opcional" />
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          <Button type="button" size="sm" variant="ghost" data-testid={`po-manual-remove-${idx}`} onClick={() => removeManualLine(l.id)} className="text-zinc-500 hover:text-red-400 h-8 px-2">
                            ✕
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {scope === "proyecto_completo" && (
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-400 mb-3">Se creará un único ítem &quot;Proyecto completo · Stand {stand.stand_number}&quot; con costo global.</div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Costo total del proyecto</Label>
                <Input type="number" className={inputCls} value={itemsData.__full__?.costo_unit || ""} onChange={(e) => setItemsData((d) => ({ ...d, __full__: { costo_unit: e.target.value } }))} />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Fecha vencimiento saldo</Label>
            <Input type="date" className={inputCls} value={fechaVenc} onChange={(e) => setFechaVenc(e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Condición de pago</Label>
            <Input className={inputCls} value={condPago} onChange={(e) => setCondPago(e.target.value)} placeholder="Ej: 50/50, transferencia" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Observaciones para el proveedor</Label>
          <Textarea className={inputCls} rows={2} value={obsProv} onChange={(e) => setObsProv(e.target.value)} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Observaciones internas (nunca al proveedor)</Label>
          <Textarea className={inputCls} rows={2} value={obsInt} onChange={(e) => setObsInt(e.target.value)} />
        </div>

        <div className="flex justify-end">
          <Button data-testid="po-create-submit" onClick={submit} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear pedido"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
